// File: src/components/admin/autopilot/AutopilotListView.js
// Copyright (c) 2025 DefoAI UG (haftungsbeschraenkt)
// All rights reserved.
//
// Admin list view for Autopilot user management

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Paper, Typography, Chip, TextField, MenuItem, Select,
    FormControl, InputLabel, LinearProgress, IconButton, Tooltip, Grid,
    CircularProgress, Snackbar, Alert,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import {
    AutoFixHigh as AutoFixHighIcon,
    Pause as PauseIcon,
    PlayArrow as PlayArrowIcon,
    Visibility as VisibilityIcon,
    Search as SearchIcon,
    Error as ErrorIcon,
} from '@mui/icons-material';
import { adminApi, handleAdminApiError } from '../../../services/adminApi';

const STATUS_OPTIONS = ['active', 'learning', 'paused'];
const MODE_OPTIONS = ['conservative', 'balanced', 'aggressive'];

const STATUS_COLORS = {
    active: 'success',
    learning: 'info',
    paused: 'warning',
};

const MODE_COLORS = {
    conservative: 'default',
    balanced: 'primary',
    aggressive: 'error',
};

function StatCard({ title, value, color }) {
    return (
        <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">{title}</Typography>
            <Typography variant="h4" fontWeight={700} color={color}>{value}</Typography>
        </Paper>
    );
}

function formatRelativeTime(dateString) {
    if (!dateString) return '-';
    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
}

const AutopilotListView = () => {
    const navigate = useNavigate();

    // Data state
    const [users, setUsers] = useState([]);
    const [overview, setOverview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Filter state
    const [statusFilter, setStatusFilter] = useState([]);
    const [modeFilter, setModeFilter] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [errorFilter, setErrorFilter] = useState('');

    // Snackbar state
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Fetch all data
    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = {};
            if (statusFilter.length > 0) params.status = statusFilter.join(',');
            if (modeFilter.length > 0) params.mode = modeFilter.join(',');
            if (searchText.trim()) params.search = searchText.trim();
            if (errorFilter) params.hasErrors = errorFilter === 'errors';

            const [overviewData, usersData] = await Promise.all([
                adminApi.getAutopilotOverview().catch(() => null),
                adminApi.getAutopilotUsers(params),
            ]);
            setOverview(overviewData);
            setUsers(usersData.users || usersData || []);
        } catch (err) {
            const errorInfo = handleAdminApiError(err);
            setError(errorInfo);
        } finally {
            setLoading(false);
        }
    }, [statusFilter, modeFilter, searchText, errorFilter]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Handle pause/resume toggle
    const handleToggleStatus = async (user) => {
        const action = user.status === 'paused' ? 'resume' : 'pause';
        try {
            await adminApi.updateAutopilotUser(user.uid, { action });
            setSnackbar({
                open: true,
                message: `Autopilot ${action === 'pause' ? 'paused' : 'resumed'} for ${user.email}`,
                severity: 'success',
            });
            fetchData();
        } catch (err) {
            const { message } = handleAdminApiError(err);
            setSnackbar({ open: true, message: `Failed to ${action}: ${message}`, severity: 'error' });
        }
    };

    // Handle snackbar close
    const handleSnackbarClose = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    if (loading && users.length === 0) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box data-testid="autopilot-list-view">
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AutoFixHighIcon color="primary" />
                    <Typography variant="h5" fontWeight={600}>
                        Autopilot Dashboard
                    </Typography>
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error.message}
                </Alert>
            )}

            {/* Overview Stat Cards */}
            {overview && (
                <Grid container spacing={2} sx={{ mb: 3 }} data-testid="autopilot-overview-cards">
                    <Grid item xs={12} sm={6} md={3}>
                        <StatCard
                            title="Total Users"
                            value={overview.totalUsers ?? 0}
                            color="#1976d2"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <StatCard
                            title="Active / Learning / Paused"
                            value={`${overview.activeUsers ?? 0} / ${overview.learningUsers ?? 0} / ${overview.pausedUsers ?? 0}`}
                            color="#2e7d32"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <StatCard
                            title="Changes Today"
                            value={overview.changesToday ?? 0}
                            color="#9c27b0"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <StatCard
                            title="Error Rate 24h"
                            value={overview.errorRate24h != null ? `${overview.errorRate24h}%` : '0%'}
                            color={overview.errorRate24h > 0 ? '#d32f2f' : '#757575'}
                        />
                    </Grid>
                </Grid>
            )}

            {/* Filter Bar */}
            <Paper sx={{ p: 2, mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                        multiple
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        label="Status"
                        renderValue={(selected) => selected.join(', ')}
                        data-testid="autopilot-filter-status"
                    >
                        {STATUS_OPTIONS.map((s) => (
                            <MenuItem key={s} value={s}>{s}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Mode</InputLabel>
                    <Select
                        multiple
                        value={modeFilter}
                        onChange={(e) => setModeFilter(e.target.value)}
                        label="Mode"
                        renderValue={(selected) => selected.join(', ')}
                        data-testid="autopilot-filter-mode"
                    >
                        {MODE_OPTIONS.map((m) => (
                            <MenuItem key={m} value={m}>{m}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <TextField
                    size="small"
                    placeholder="Search by email or name"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    InputProps={{
                        startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} fontSize="small" />,
                    }}
                    sx={{ minWidth: 220 }}
                    data-testid="autopilot-filter-search"
                />

                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Error State</InputLabel>
                    <Select
                        value={errorFilter}
                        onChange={(e) => setErrorFilter(e.target.value)}
                        label="Error State"
                        data-testid="autopilot-filter-errors"
                    >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="errors">With Errors</MenuItem>
                        <MenuItem value="no-errors">No Errors</MenuItem>
                    </Select>
                </FormControl>
            </Paper>

            {/* Users Table */}
            <TableContainer component={Paper} data-testid="autopilot-user-table">
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>User</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Mode</TableCell>
                            <TableCell align="center">Campaigns</TableCell>
                            <TableCell align="center">Changes Today</TableCell>
                            <TableCell>Last Cycle</TableCell>
                            <TableCell>Monthly Spend</TableCell>
                            <TableCell align="center">Errors 7d</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {users.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={9} align="center">
                                    <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                                        No autopilot users found matching the current filters.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                        {users.map((user) => (
                            <TableRow
                                key={user.uid}
                                hover
                                sx={{ cursor: 'pointer' }}
                                onDoubleClick={() => navigate(`/autopilot/${user.uid}`)}
                            >
                                <TableCell>
                                    <Typography
                                        variant="body2"
                                        component="a"
                                        href={`/autopilot/${user.uid}`}
                                        onClick={(e) => { e.preventDefault(); navigate(`/autopilot/${user.uid}`); }}
                                        sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                                    >
                                        {user.email}
                                    </Typography>
                                    {user.name && (
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            {user.name}
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={user.status || 'unknown'}
                                        color={STATUS_COLORS[user.status] || 'default'}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={user.mode || 'unknown'}
                                        color={MODE_COLORS[user.mode] || 'default'}
                                        size="small"
                                        variant="outlined"
                                    />
                                </TableCell>
                                <TableCell align="center">
                                    <Typography variant="body2">{user.campaignCount ?? '-'}</Typography>
                                </TableCell>
                                <TableCell align="center">
                                    <Typography variant="body2">{user.changesToday ?? 0}</Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2">{formatRelativeTime(user.lastCycleAt)}</Typography>
                                </TableCell>
                                <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 120 }}>
                                        <LinearProgress
                                            variant="determinate"
                                            value={Math.min(((user.monthlySpend ?? 0) / (user.monthlyBudget || 1)) * 100, 100)}
                                            sx={{ flex: 1, height: 8, borderRadius: 4 }}
                                        />
                                        <Typography variant="caption" color="text.secondary" noWrap>
                                            ${user.monthlySpend ?? 0}
                                        </Typography>
                                    </Box>
                                </TableCell>
                                <TableCell align="center">
                                    <Typography
                                        variant="body2"
                                        fontWeight={user.errors7d > 0 ? 700 : 400}
                                        color={user.errors7d > 0 ? 'error.main' : 'text.primary'}
                                    >
                                        {user.errors7d ?? 0}
                                        {user.errors7d > 0 && (
                                            <ErrorIcon sx={{ ml: 0.5, fontSize: 14, verticalAlign: 'middle' }} color="error" />
                                        )}
                                    </Typography>
                                </TableCell>
                                <TableCell align="right">
                                    <Tooltip title="View details">
                                        <IconButton
                                            size="small"
                                            color="primary"
                                            onClick={(e) => { e.stopPropagation(); navigate(`/autopilot/${user.uid}`); }}
                                            data-testid={`autopilot-btn-view-${user.uid}`}
                                        >
                                            <VisibilityIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    {user.status === 'paused' ? (
                                        <Tooltip title="Resume autopilot">
                                            <IconButton
                                                size="small"
                                                color="success"
                                                onClick={(e) => { e.stopPropagation(); handleToggleStatus(user); }}
                                                data-testid={`autopilot-btn-resume-${user.uid}`}
                                            >
                                                <PlayArrowIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    ) : (
                                        <Tooltip title="Pause autopilot">
                                            <IconButton
                                                size="small"
                                                color="warning"
                                                onClick={(e) => { e.stopPropagation(); handleToggleStatus(user); }}
                                                data-testid={`autopilot-btn-pause-${user.uid}`}
                                            >
                                                <PauseIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleSnackbarClose} severity={snackbar.severity} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default AutopilotListView;
